(() => {
  if (customElements.get('bottom-text-customizer')) return;

  const PreviewRenderer = window.LEDTextPreviewRenderer;
  if (!PreviewRenderer) return;
  const {
    sizeRules, productColors, previewFontFamily, contourCutClearanceCm,
    tubeDiameterCm, artworkTextGapCm, textOccupancyRatio
  } = PreviewRenderer;

  class BottomTextCustomizer extends HTMLElement {
    connectedCallback() {
      // Theme sections can be inserted after the asset has already loaded.
      // Wait until the element's children exist before collecting references.
      this.initializeFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      if (this.initialized) return;
      this.initialized = true;

      this.form = document.getElementById(this.dataset.formId);
      this.productInfo = this.closest('product-info');
      this.formError = this.form?.querySelector('.product-form__error-message');
      this.buyNowButton = document.querySelector(`[data-bottom-text-buy-now][form="${this.dataset.formId}"]`);
      this.textInput = this.querySelector('[data-bottom-text-input]');
      this.gallery = document.getElementById(this.dataset.galleryId);
      this.mediaPreviews = this.gallery
        ? Array.from(this.gallery.querySelectorAll('[data-bottom-text-media-preview]'))
        : [];
      this.characterCount = this.querySelector('[data-character-count]');
      this.errorElement = this.querySelector('[data-bottom-text-error]');
      this.bundleInput = this.querySelector('[data-fee-bundle]');
      this.propertyInputs = Object.fromEntries(
        Array.from(this.querySelectorAll('[data-property]')).map((input) => [input.dataset.property, input])
      );
      this.productVariants = this.parseJson('[data-product-variants]', []);
      this.feeVariants = this.parseJson('[data-fee-variants]', {});
      this.artworkJoinProfile = this.readArtworkJoinProfile();
      this.currentVariant = this.findCurrentVariant();
      this.invalidCharacterFound = false;
      this.customizationId = '';
      this.nativeTextMeasureCache = new Map();
      this.glyphGeometryCache = new Map();
      this.singleLineGlyphData = window.BottomTextSingleLineFont || {};
      this.dataset.previewRevision = '2026-09-28.6';

      this.onTextInput = (event) => {
        if (!event.target.matches('[data-bottom-text-input]')) return;
        this.textInput = event.target;
        this.handleTextInput();
      };
      this.onTextBlur = (event) => {
        if (!event.target.matches('[data-bottom-text-input]')) return;
        this.textInput = event.target;
        this.normalizeText();
      };
      this.addEventListener('input', this.onTextInput);
      this.addEventListener('focusout', this.onTextBlur);
      this.onVariantChange = (event) => this.handleVariantChange(event);
      this.onFormChange = (event) => {
        if (event.target.matches('[name="id"]')) {
          this.currentVariant = this.findCurrentVariant();
          this.update();
        } else if (event.target.matches('[name="quantity"]')) {
          this.update();
        }
      };
      this.onFormSubmit = (event) => this.handleSubmit(event);
      this.onProductOptionChange = (event) => this.handleProductOptionChange(event);
      this.form?.addEventListener('variant:change', this.onVariantChange);
      this.form?.addEventListener('change', this.onFormChange);
      this.form?.addEventListener('submit', this.onFormSubmit, true);
      document.addEventListener('change', this.onProductOptionChange, true);
      this.onProductColorChange = (event) => this.handleProductColorChange(event);
      this.onProductColorClick = (event) => this.handleProductColorClick(event);
      document.addEventListener('change', this.onProductColorChange);
      document.addEventListener('click', this.onProductColorClick);
      this.onBuyNow = (event) => this.handleBuyNow(event);
      this.buyNowButton?.addEventListener('click', this.onBuyNow);

      this.onPreviewResize = () => {
        if (this.previewResizeFrame) cancelAnimationFrame(this.previewResizeFrame);
        this.previewResizeFrame = requestAnimationFrame(() => {
          this.previewResizeFrame = 0;
          this.updatePreviewScale();
        });
      };
      window.addEventListener('resize', this.onPreviewResize, { passive: true });

      document.fonts?.ready.then(() => {
        this.nativeTextMeasureCache.clear();
        this.glyphGeometryCache.clear();
        this.updatePreviewScale();
      });
      this.update();
    }

    disconnectedCallback() {
      if (this.initializeFrame) cancelAnimationFrame(this.initializeFrame);
      if (this.previewRenderFrame) cancelAnimationFrame(this.previewRenderFrame);
      if (this.previewResizeFrame) cancelAnimationFrame(this.previewResizeFrame);
      this.buyNowButton?.removeEventListener('click', this.onBuyNow);
      this.form?.removeEventListener('variant:change', this.onVariantChange);
      this.form?.removeEventListener('change', this.onFormChange);
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      document.removeEventListener('change', this.onProductOptionChange, true);
      document.removeEventListener('change', this.onProductColorChange);
      document.removeEventListener('click', this.onProductColorClick);
      window.removeEventListener('resize', this.onPreviewResize);
      this.removeEventListener('input', this.onTextInput);
      this.removeEventListener('focusout', this.onTextBlur);
      this.initialized = false;
    }

    parseJson(selector, fallback) {
      const element = this.querySelector(selector);
      if (!element) return fallback;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.warn('Unable to parse bottom-text configuration', error);
        return fallback;
      }
    }

    readArtworkJoinProfile() {
      const fallback = [[0, 1], [1, 1]];
      try {
        const profile = JSON.parse(this.dataset.artworkJoinProfile || 'null');
        if (!Array.isArray(profile) || profile.length < 2 || profile.length > 100) return fallback;
        if (!profile.every((point, index) => Array.isArray(point) && point.length === 2
          && point.every((value) => Number.isFinite(value) && value >= 0 && value <= 1)
          && (index === 0 || point[0] > profile[index - 1][0]))) return fallback;
        return profile[0][0] === 0 && profile[profile.length - 1][0] === 1 ? profile : fallback;
      } catch {
        return fallback;
      }
    }

    attachFeeToProduct(payload) {
      if (!this.customizationId || !Array.isArray(payload?.items)) return payload;
      const matches = (item) => item.properties?.['_Customization ID'] === this.customizationId;
      const parent = payload.items.find((item) =>
        String(item.id) === String(this.currentVariant?.id) && matches(item));
      const fee = payload.items.find((item) => item.properties?.['_LED Text Fee'] === 'true' && matches(item));
      if (parent && fee) {
        fee.parent_id = Number(parent.id);
        fee.quantity = Math.max(1, Number(parent.quantity || 1));
        payload.items = [parent, fee, ...payload.items.filter((item) => item !== parent && item !== fee)];
      }
      return payload;
    }

    findCurrentVariant() {
      const selectedVariant = this.findSelectedVariant();
      if (selectedVariant) return selectedVariant;
      const variantId = this.form?.querySelector('[name="id"]')?.value;
      return this.productVariants.find((variant) => String(variant.id) === String(variantId)) || this.productVariants[0] || null;
    }

    findSelectedVariant(picker = this.productInfo?.querySelector('variant-picker')) {
      if (!picker) return null;
      const selectedOptions = new Map();
      picker.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = Number(group.dataset.optionIndex.replace('option', '')) - 1;
        const input = group.querySelector('input:checked, select');
        if (index >= 0 && input) selectedOptions.set(index, input.value);
      });
      if (!selectedOptions.size) return null;
      return this.productVariants.find((variant) =>
        variant.options?.length === selectedOptions.size
        && variant.options.every((value, index) => value === selectedOptions.get(index))
      ) || null;
    }

    handleProductOptionChange(event) {
      const picker = event.target.closest?.('variant-picker');
      if (!picker || picker.closest('product-info') !== this.productInfo) return;
      if (!event.target.matches('input[type="radio"], select')) return;
      const variant = this.findSelectedVariant(picker);
      if (!variant) return;
      this.currentVariant = variant;
      picker.querySelectorAll('[data-option-index]').forEach((group) => {
        const input = group.querySelector('input:checked, select');
        const label = group.querySelector('.form__label .font-medium');
        if (input && label) label.textContent = input.value;
      });
      this.productInfo.updateVariantInputs?.(variant.id);
      this.productInfo.updateURL?.(variant.id);
      this.update({ deferPreview: true });
    }

    handleVariantChange(event) {
      const selectedVariant = this.findSelectedVariant();
      const variant = event.detail?.variant;
      if (variant && (!selectedVariant || String(variant.id) === String(selectedVariant.id))) {
        this.currentVariant = {
          ...this.productVariants.find((item) => String(item.id) === String(variant.id)),
          ...variant
        };
      } else {
        this.currentVariant = selectedVariant || this.findCurrentVariant();
      }
      this.refreshMediaPreviews();
      this.update();
    }

    refreshMediaPreviews() {
      const gallery = document.getElementById(this.dataset.galleryId);
      const mediaPreviews = gallery
        ? Array.from(gallery.querySelectorAll('[data-bottom-text-media-preview]'))
        : [];
      const unchanged = gallery === this.gallery
        && mediaPreviews.length === this.mediaPreviews.length
        && mediaPreviews.every((preview, index) => preview === this.mediaPreviews[index]);

      if (unchanged) return;

      this.gallery = gallery;
      this.mediaPreviews = mediaPreviews;

    }

    handleTextInput() {
      const originalValue = this.textInput.value;
      const sanitizedValue = originalValue.replace(/[^\x20-\x7E]/g, '');
      this.invalidCharacterFound = sanitizedValue !== originalValue;

      if (sanitizedValue !== originalValue) {
        const selectionStart = this.textInput.selectionStart;
        this.textInput.value = sanitizedValue;
        const nextPosition = Math.max(0, (selectionStart || 0) - (originalValue.length - sanitizedValue.length));
        this.textInput.setSelectionRange(nextPosition, nextPosition);
      }

      this.update({ deferPreview: true });
    }

    handleProductColorChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name !== 'properties[Color]' || input.form !== this.form) return;
      this.update();
    }

    handleProductColorClick(event) {
      const button = event.target.closest?.('[data-pin2-color-value]');
      if (!button) return;
      const colorField = button.closest('[data-pin2-color-field]');
      if (colorField?.dataset.pin2ColorField !== this.dataset.formId) return;
      requestAnimationFrame(() => this.update());
    }

    normalizeText() {
      this.textInput.value = this.textInput.value.trim();
      this.invalidCharacterFound = false;
      this.update();
    }

    handleSubmit(event) {
      this.normalizeText();
      const state = this.getState();
      if (state.blocked || state.active) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      if (state.blocked) {
        this.textInput.focus();
      } else if (state.active) {
        this.addCustomizedItems(state, false, event.submitter);
      }
    }

    async handleBuyNow(event) {
      event.preventDefault();
      this.normalizeText();
      const state = this.getState();
      if (state.blocked) {
        this.textInput.focus();
        return;
      }
      await this.addCustomizedItems(state, true, this.buyNowButton);
    }

    async addCustomizedItems(state, checkout, button) {
      if (!this.form || !this.currentVariant?.available || this.cartSubmitting) return;
      this.cartSubmitting = true;
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      if (this.formError) this.formError.hidden = true;

      try {
        if (state.active) {
          this.customizationId = '';
          this.syncProperties(state);
          this.syncBundle(state);
        }
        const payload = this.buildCartPayload(new FormData(this.form), state);
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Cart request failed');
        window.location.assign(root + (checkout ? 'checkout' : 'cart'));
      } catch (error) {
        console.error('Unable to add LED customization', error);
        if (this.formError) {
          this.formError.textContent = checkout
            ? this.dataset.buyNowErrorMessage
            : this.dataset.cartErrorMessage;
          this.formError.hidden = false;
        }
      } finally {
        this.cartSubmitting = false;
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
        this.setSubmitBlocked(this.getState().blocked);
        this.syncBuyNowButton(this.getState());
      }
    }

    buildCartPayload(formData, state) {
      const quantity = Math.max(1, Number(formData.get('quantity') || 1));
      const properties = {};

      for (const [name, value] of formData.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }

      if (state.active) properties['_LED Preview'] = JSON.stringify(this.getCartPreviewConfiguration(state));

      const items = [];
      if (state.active) {
        items.push({
          id: state.feeVariantId,
          quantity,
          properties: {
            '_Customization ID': this.customizationId,
            '_Parent Product ID': this.dataset.productId,
            '_LED Text Fee': 'true',
            '_Bottom Text': state.text
          }
        });
      }

      const productItem = {
        id: this.currentVariant.id,
        quantity
      };
      if (Object.keys(properties).length > 0) productItem.properties = properties;
      items.push(productItem);

      return this.attachFeeToProduct({ items });
    }

    getSizeInches() {
      const title = this.currentVariant?.title || '';
      const match = title.match(/(20|30|35|40|45|50|60)\s*in/i);
      return match ? Number(match[1]) : 20;
    }

    getProductColor() {
      const colorInputs = Array.from(this.form?.elements || []).filter((element) => element.name === 'properties[Color]');
      const propertyInput = colorInputs.find((input) => input.type === 'radio' && input.checked)
        || colorInputs.find((input) => input.type !== 'radio');
      const colorOptionIndex = Number(this.dataset.colorOptionIndex);
      const variantColor = Number.isInteger(colorOptionIndex) && colorOptionIndex >= 0
        ? this.currentVariant?.options?.[colorOptionIndex]
        : '';
      const name = String(propertyInput?.value || variantColor || this.dataset.defaultColor || 'Pink').trim();

      return {
        name,
        value: productColors[name.toLowerCase()] || productColors.pink
      };
    }

    getState() {
      this.textInput = this.querySelector('[data-bottom-text-input]');
      const text = this.textInput.value.trim();
      const characterCount = Array.from(text).length;
      const sizeInches = this.getSizeInches();
      const rules = sizeRules[sizeInches] || sizeRules[20];
      const overLimit = characterCount > rules.maxCharacters;
      // Each fee variant represents all letters on one sign.
      const fee = this.feeVariants[rules.characterWidthCm + '|' + characterCount];
      const feeCents = characterCount * rules.rateCents;
      const validFee = fee?.id && fee.available === true
        && Number(fee.price) === feeCents
        && Number(fee.compare_at_price) === Math.round(feeCents / 0.85);
      const feeVariantId = validFee ? String(fee.id) : '';
      const missingFeeVariant = characterCount > 0 && !overLimit && !validFee;
      const blocked = overLimit || this.invalidCharacterFound || missingFeeVariant;
      const active = characterCount > 0 && !blocked;
      const textLayout = this.getTextLayout(text, rules, characterCount);

      return {
        active,
        blocked,
        missingFeeVariant,
        characterCount,
        color: this.getProductColor(),
        feeCents: characterCount * rules.rateCents,
        feeVariantId,
        font: 'Arial',
        rules,
        sizeInches,
        text,
        textLayout,
        layout: ['round', 'vertical', 'horizontal'].includes(this.dataset.layout)
          ? this.dataset.layout
          : 'horizontal'
      };
    }

    update(options = {}) {
      const state = this.getState();
      this.characterCount.textContent = `${state.characterCount} / ${state.rules.maxCharacters}`;
      this.textInput.maxLength = state.rules.maxCharacters;
      this.dataset.selectedVariantId = String(this.currentVariant?.id || '');
      this.dataset.selectedSizeInches = String(state.sizeInches);
      this.dataset.maxCharacters = String(state.rules.maxCharacters);
      this.dataset.feeVariantId = state.feeVariantId;
      this.dataset.feeReady = String(!state.missingFeeVariant);

      let errorMessage = '';
      if (this.invalidCharacterFound) {
        errorMessage = this.dataset.invalidMessage;
      } else if (state.characterCount > state.rules.maxCharacters) {
        errorMessage = this.dataset.limitMessage.replace('[max]', String(state.rules.maxCharacters));
      } else if (state.missingFeeVariant) {
        errorMessage = this.dataset.feeErrorMessage || 'Custom text is temporarily unavailable. Please try again or contact us.';
      }

      this.errorElement.textContent = errorMessage;
      this.errorElement.hidden = !errorMessage;
      this.textInput.setAttribute('aria-invalid', errorMessage ? 'true' : 'false');

      if (options.deferPreview) {
        if (this.previewRenderFrame) cancelAnimationFrame(this.previewRenderFrame);
        this.previewRenderFrame = requestAnimationFrame(() => {
          this.previewRenderFrame = 0;
          this.updateMediaPreview(this.getState());
        });
      } else {
        this.updateMediaPreview(state);
      }
      this.syncDisplayedPrice(state);

      this.syncProperties(state);
      this.syncBundle(state);
      this.setSubmitBlocked(state.blocked);
      this.syncBuyNowButton(state);
    }

    updateMediaPreview(state) {
      this.refreshMediaPreviews();

      this.mediaPreviews.forEach((preview) => {
        preview.hidden = !state.text;
        preview.style.setProperty('--preview-color', state.color.value);
        preview.style.setProperty('--preview-font-family', previewFontFamily);
      });

      this.updatePreviewScale(state);
    }

    updatePreviewScale(state = this.getState()) {
      if (this.mediaPreviews.length === 0) return;
      this.mediaPreviews.forEach((preview) => {
        const parent = preview.parentElement;
        const parentRect = parent?.getBoundingClientRect?.();
        const width = Math.max(1, parentRect?.width || parent?.clientWidth || this.gallery?.clientWidth || 580);
        const height = Math.max(1, parentRect?.height || parent?.clientHeight || width);
        const artwork = {
          centerX: this.readRatio(this.dataset.artworkCenterXRatio, 0.5) * width,
          centerY: this.readRatio(this.dataset.artworkCenterYRatio, 0.5) * height,
          width: this.readRatio(this.dataset.artworkWidthRatio, 0.7, 0.05) * width,
          height: this.readRatio(this.dataset.artworkHeightRatio, 0.7, 0.05) * height
        };
        const referenceSpan = state.layout === 'vertical' ? artwork.height : artwork.width;
        const pixelsPerCm = referenceSpan / state.rules.originalLongestSideCm;
        const uniformScale = state.textLayout.uniformScale;
        const characterAdvance = pixelsPerCm * state.textLayout.renderedCellCm;
        const fontSize = characterAdvance * 0.94;
        // Tube and acrylic clearance are physical dimensions. They do not
        // become thicker when switching from the 6 cm to the 10 cm tier.
        const tubeDiameter = pixelsPerCm * tubeDiameterCm;
        const gap = pixelsPerCm * artworkTextGapCm;

        preview.dataset.previewLayout = state.layout;
        preview.dataset.previewReferenceSpanPx = referenceSpan.toFixed(2);
        preview.dataset.previewCharacterAdvancePx = characterAdvance.toFixed(2);
        preview.dataset.previewFontSizePx = fontSize.toFixed(2);
        preview.dataset.previewUniformScale = uniformScale.toFixed(4);
        preview.dataset.previewTextOccupancyRatio = textOccupancyRatio.toFixed(2);
        preview.dataset.previewArtworkGapPx = gap.toFixed(2);
        preview.dataset.previewTextScaleX = '1.0000';
        preview.dataset.previewTextScaleY = '1.0000';
        preview.dataset.previewTubeDiameterCm = String(tubeDiameterCm);
        preview.dataset.previewTubeDiameterPx = tubeDiameter.toFixed(3);
        preview.dataset.previewRenderedCellCm = state.textLayout.renderedCellCm.toFixed(3);
        preview.dataset.previewReservedSpanCm = state.textLayout.renderedWidthCm.toFixed(3);
        preview.dataset.previewBackboardClearanceCm = String(contourCutClearanceCm);
        preview.dataset.previewBackboardContour = 'smooth-text-envelope';
        preview.dataset.previewGlyphHeightsCm = JSON.stringify(Object.fromEntries(
          [...new Set(Array.from(state.text))].filter((character) => character !== ' ').map((character) => {
            const glyph = this.getGlyphGeometry(character);
            const height = glyph
              ? (glyph.maxY - glyph.minY) * glyph.sourceScale
                * (state.textLayout.renderedCellCm * 0.94 * 0.88 / glyph.capAscent) + tubeDiameterCm
              : 0;
            return [character, Number(height.toFixed(3))];
          })
        ));

        if (state.text) {
          this.renderLayoutPreview(preview, state, {
            artwork,
            characterAdvance,
            fontSize,
            gap,
            height,
            pixelsPerCm,
            tubeDiameter,
            width
          });
        }
      });
    }

    getCartPreviewConfiguration(state) {
      const preview = this.mediaPreviews.find((element) => element.parentElement?.clientWidth > 0)
        || this.mediaPreviews[0];
      const parent = preview?.parentElement;
      const image = parent?.querySelector('img');
      const frame = parent?.getBoundingClientRect();
      const imageStyle = image ? window.getComputedStyle(image) : null;
      const source = image?.currentSrc || image?.src || this.dataset.previewImage || '';
      const imageUrl = (() => {
        try {
          const url = new URL(source, window.location.href);
          url.searchParams.set('width', '960');
          return url.href;
        } catch {
          return this.dataset.previewImage || '';
        }
      })();
      return {
        v: 1,
        image: imageUrl,
        aspect: Number(((frame?.width && frame?.height)
          ? frame.width / frame.height
          : Number(this.dataset.previewImageAspect) || 1).toFixed(5)),
        fit: ['contain', 'cover', 'fill'].includes(imageStyle?.objectFit) ? imageStyle.objectFit : 'contain',
        position: imageStyle?.objectPosition || '50% 50%',
        text: state.text,
        color: state.color.name,
        size: state.sizeInches,
        layout: state.layout,
        artwork: [
          this.readRatio(this.dataset.artworkCenterXRatio, 0.5),
          this.readRatio(this.dataset.artworkCenterYRatio, 0.5),
          this.readRatio(this.dataset.artworkWidthRatio, 0.7, 0.05),
          this.readRatio(this.dataset.artworkHeightRatio, 0.7, 0.05)
        ],
        profile: this.artworkJoinProfile
      };
    }

    syncProperties(state) {
      const signature = JSON.stringify([this.currentVariant?.id, state.text, state.color.name]);
      if (state.active && (!this.customizationId || signature !== this.customizationSignature)) {
        this.customizationId = window.crypto?.randomUUID?.()
          || 'bottom-' + Date.now() + '-' + Math.random().toString(16).slice(2);
        this.customizationSignature = signature;
      }

      if (!state.text) {
        this.customizationId = '';
        this.customizationSignature = '';
      }

      // The external script can be evaluated while the custom element's
      // remaining child nodes are still being parsed. Re-collect these inputs
      // on every sync so cart properties cannot be lost because of an early
      // connectedCallback.
      this.propertyInputs = Object.fromEntries(
        Array.from(this.querySelectorAll('[data-property]')).map((input) => [input.dataset.property, input])
      );

      const values = {
        text: state.text,
        font: state.font,
        color: state.color.name,
        characters: String(state.characterCount),
        layout: state.layout,
        width: `${state.rules.characterWidthCm} cm`,
        height: `${state.rules.characterHeightCm} cm`,
        preview_scale: state.textLayout.uniformScale.toFixed(4),
        preview_cell: `${state.textLayout.renderedCellCm.toFixed(2)} cm`,
        preview_span: `${state.textLayout.renderedWidthCm.toFixed(2)} cm`,
        construction: '5 mm LED neon tube',
        backboard: 'Clear acrylic, smooth continuous contour with approximately 1 cm clearance outside the LED tube, fully connected to the original product backboard',
        size: this.currentVariant?.title || `${state.sizeInches}in`,
        customization_id: this.customizationId,
        cart_preview: state.active ? JSON.stringify(this.getCartPreviewConfiguration(state)) : ''
      };

      this.dataset.syncedText = values.text;
      this.dataset.syncedColor = values.color;
      this.dataset.syncedLayout = values.layout;
      this.dataset.syncedCharacterCount = values.characters;
      this.dataset.syncedPropertyCount = String(Object.keys(this.propertyInputs).length);

      Object.entries(this.propertyInputs).forEach(([key, input]) => {
        const value = values[key] || '';
        input.value = value;
        input.defaultValue = value;
        input.setAttribute('value', value);
        input.disabled = !state.active;
      });
    }

    syncBundle(state) {
      if (!this.bundleInput) return;

      this.bundleInput.value = state.feeVariantId;
      this.bundleInput.checked = state.active;
      this.bundleInput.disabled = !state.active;
      this.bundleInput.dataset.bundleQuantity = 'match-product';
      this.bundleInput.dataset.bundleCharacters = '1';
      this.bundleInput.dataset.bundleProperties = state.active
        ? JSON.stringify({
            '_Customization ID': this.customizationId,
            '_Parent Product ID': this.dataset.productId,
            '_LED Text Fee': 'true',
            '_Bottom Text': state.text
          })
        : '';
    }

    setSubmitBlocked(blocked) {
      if (!this.form) return;
      const selector = `[type="submit"][form="${CSS.escape(this.dataset.formId)}"], #${CSS.escape(this.dataset.formId)} [type="submit"]`;
      document.querySelectorAll(selector).forEach((button) => {
        if (blocked) {
          button.dataset.bottomTextBlocked = 'true';
          button.setAttribute('aria-disabled', 'true');
          button.disabled = true;
          return;
        }

        if (button.dataset.bottomTextBlocked === 'true') {
          delete button.dataset.bottomTextBlocked;
          if (this.currentVariant?.available) {
            button.removeAttribute('aria-disabled');
            button.disabled = false;
          }
        }
      });
    }

    syncBuyNowButton(state) {
      if (!this.buyNowButton || this.buyNowButton.dataset.bottomTextLoading === 'true') return;
      const blocked = state.blocked || !this.currentVariant?.available;
      this.buyNowButton.disabled = blocked;
      this.buyNowButton.toggleAttribute('aria-disabled', blocked);
    }

    syncDisplayedPrice(state) {
      const basePriceCents = Number(this.currentVariant?.price || 0);
      const baseCompareAtCents = Number(this.currentVariant?.compare_at_price || 0);
      const totalPriceCents = basePriceCents + (state.active ? state.feeCents : 0);
      const discountedFeeCompareAtCents = state.active ? Math.round(state.feeCents / 0.85) : 0;
      const totalCompareAtCents = baseCompareAtCents > basePriceCents
        ? baseCompareAtCents + discountedFeeCompareAtCents
        : 0;
      const formattedPrice = this.formatMoney(totalPriceCents);
      const formattedCompareAtPrice = totalCompareAtCents > totalPriceCents
        ? this.formatMoney(totalCompareAtCents)
        : '';
      const priceContainers = [
        document.getElementById(this.dataset.priceId),
        document.getElementById(this.dataset.stickyPriceId)
      ].filter(Boolean);

      priceContainers.forEach((container) => {
        container.querySelectorAll('.price__regular').forEach((price) => {
          price.textContent = formattedPrice;
        });
        container.querySelectorAll('.price__sale').forEach((price) => {
          if (formattedCompareAtPrice) price.textContent = formattedCompareAtPrice;
        });
      });

      const buyButtonPrice = document.getElementById(this.dataset.buyButtonPriceId)?.querySelector('product-buy-price');
      if (buyButtonPrice) {
        buyButtonPrice.textContent = formattedPrice;
        buyButtonPrice.dataset.price = String(totalPriceCents);
      }
    }

    formatMoney(cents) {
      const currencySettings = window.theme?.settings;
      const currencyFormatter = window.theme?.Currency?.formatMoney;
      const moneyFormat = currencySettings?.currencyCodeEnabled
        ? currencySettings.moneyWithCurrencyFormat
        : currencySettings?.moneyFormat;
      if (currencyFormatter && moneyFormat) return currencyFormatter(cents, moneyFormat);

      const currency = this.dataset.currency || 'USD';
      const locale = document.documentElement.lang || 'en-US';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }
  }

  Object.defineProperties(BottomTextCustomizer.prototype,
    Object.fromEntries(Object.entries(Object.getOwnPropertyDescriptors(PreviewRenderer.prototype))
      .filter(([name]) => name !== 'constructor')));
  customElements.define('bottom-text-customizer', BottomTextCustomizer);
})();

